import { toggleStaffPermission } from '../../../../services/accessManagement/mainPageRestriction';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FrontendStaffUser,
  deleteStaffUser,
  removeRestaurantFromStaff,
  updateStaffPermissions,
} from '../../../../services/staff/staffService';
import {
  updateStaffPriceAlertPreference,
  updateStaffStaffSignupEmailPreference,
  updateStaffWeeklyReportsPreference,
} from '../../../../services/user/userService';
import { useStaffStore } from '../../../../stores/appStaffStore';

/**
 * Every staff mutation the page owns. Destructive actions (delete, remove access)
 * resolve to a boolean so the caller can keep its modal open on failure; the
 * preference/permission toggles update the store in place and surface failures
 * through an alert, matching the rest of the page.
 */
export function useStaffMutations() {
  const { t } = useTranslation();
  const reload = useStaffStore((s) => s.reload);
  const updateStaffInStore = useStaffStore((s) => s.updateStaffInStore);
  const [error, setError] = useState<string | null>(null);

  const deleteUser = useCallback(
    async (user: FrontendStaffUser): Promise<boolean> => {
      try {
        await deleteStaffUser(user.id);
        reload();
        return true;
      } catch (err) {
        console.error(err);
        setError(t('staff.deleteUserError'));
        return false;
      }
    },
    [reload, t]
  );

  const removeRestaurantAccess = useCallback(
    async (user: FrontendStaffUser, restaurantId: string): Promise<boolean> => {
      try {
        await removeRestaurantFromStaff(user.id, restaurantId);
        reload();
        return true;
      } catch (err) {
        console.error(err);
        setError(t('staff.removeRestaurantError'));
        return false;
      }
    },
    [reload, t]
  );

  const togglePriceAlert = useCallback(
    async (user: FrontendStaffUser, enabled: boolean) => {
      try {
        await updateStaffPriceAlertPreference(user.id, enabled);
        updateStaffInStore(user.id, { receive_price_alert_emails: enabled });
      } catch (err) {
        console.error(err);
        alert(t('staff.updatePriceAlertError'));
      }
    },
    [updateStaffInStore, t]
  );

  const toggleWeeklyReports = useCallback(
    async (user: FrontendStaffUser, enabled: boolean) => {
      try {
        await updateStaffWeeklyReportsPreference(user.id, enabled);
        updateStaffInStore(user.id, { receive_weekly_reports_emails: enabled });
      } catch (err) {
        console.error(err);
        alert(t('staff.updateWeeklyReportsError'));
      }
    },
    [updateStaffInStore, t]
  );

  const toggleStaffSignupEmail = useCallback(
    async (user: FrontendStaffUser, enabled: boolean) => {
      try {
        await updateStaffStaffSignupEmailPreference(user.id, enabled);
        updateStaffInStore(user.id, { receive_staff_signup_emails: enabled });
      } catch (err) {
        console.error(err);
        alert(t('staff.updateStaffSignupEmailError'));
      }
    },
    [updateStaffInStore, t]
  );

  const togglePermission = useCallback(
    async (user: FrontendStaffUser, permission: string, enabled: boolean) => {
      try {
        const newPermissions = toggleStaffPermission(user.permissions, permission, enabled);

        await updateStaffPermissions(user.id, newPermissions);
        updateStaffInStore(user.id, { permissions: newPermissions });
      } catch (err) {
        console.error(err);
        alert(t('staff.updatePermissionsError'));
      }
    },
    [updateStaffInStore, t]
  );

  return {
    error,
    reload,
    deleteUser,
    removeRestaurantAccess,
    togglePriceAlert,
    toggleWeeklyReports,
    toggleStaffSignupEmail,
    togglePermission,
  };
}
